import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      setSuccess(
        'Registration successful! Please wait for approval before signing in.'
      );
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-6 sm:space-y-8'>
        {/* Brand Section */}
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <div className='w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center shadow-lg'>
              <span className='text-white font-bold text-2xl'>P</span>
            </div>
          </div>
          <h2 className='heading-2 text-gray-900 mb-2'>Join PartnerGPT</h2>
          <p className='body-small text-gray-600'>
            Create your account and start your AI journey today
          </p>
        </div>

        <Card className='card-enhanced shadow-xl border-0'>
          <CardContent className='space-y-6 px-6 sm:px-8 py-8'>
            {error && (
              <div className='alert-enhanced alert-error'>
                <div className='flex items-center gap-2'>
                  <span className='text-red-500'>⚠️</span>
                  <span>{error}</span>
                </div>
              </div>
            )}

            {success && (
              <div className='alert-enhanced alert-success'>
                <div className='flex items-center gap-2'>
                  <span className='text-green-500'>✅</span>
                  <span>{success}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div className='form-field'>
                  <Label htmlFor='firstName' className='form-label'>
                    First Name
                  </Label>
                  <Input
                    id='firstName'
                    name='firstName'
                    type='text'
                    autoComplete='given-name'
                    required
                    placeholder='Enter your first name'
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={loading}
                    className='form-input focus-ring transition-smooth'
                  />
                </div>

                <div className='form-field'>
                  <Label htmlFor='lastName' className='form-label'>
                    Last Name
                  </Label>
                  <Input
                    id='lastName'
                    name='lastName'
                    type='text'
                    autoComplete='family-name'
                    required
                    placeholder='Enter your last name'
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={loading}
                    className='form-input focus-ring transition-smooth'
                  />
                </div>
              </div>

              <div className='form-field'>
                <Label htmlFor='email' className='form-label'>
                  Email Address
                </Label>
                <Input
                  id='email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  placeholder='Enter your email'
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Use your company email for faster approval
                </p>
              </div>

              <div className='form-field'>
                <Label htmlFor='password' className='form-label'>
                  Password
                </Label>
                <Input
                  id='password'
                  name='password'
                  type='password'
                  autoComplete='new-password'
                  required
                  placeholder='Create a password'
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
                <p className='text-xs text-gray-500 mt-1'>
                  Must be at least 6 characters long
                </p>
              </div>

              <div className='form-field'>
                <Label htmlFor='confirmPassword' className='form-label'>
                  Confirm Password
                </Label>
                <Input
                  id='confirmPassword'
                  name='confirmPassword'
                  type='password'
                  autoComplete='new-password'
                  required
                  placeholder='Confirm your password'
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className='form-input focus-ring transition-smooth'
                />
              </div>

              <Button
                type='submit'
                disabled={loading}
                className={`w-full btn-enhanced hover-lift focus-ring py-3 text-base font-medium ${loading ? 'btn-loading' : ''}`}
              >
                {loading ? (
                  <>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <span className='ml-2'>→</span>
                  </>
                )}
              </Button>
            </form>

            <div className='text-center text-sm pt-4 border-t border-gray-100'>
              <span className='text-gray-600'>
                Already have an account?{' '}
                <RouterLink
                  to='/login'
                  className='font-medium text-primary hover:text-primary/80 transition-colors focus-ring rounded px-1 py-0.5'
                >
                  Sign in
                </RouterLink>
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Trust indicators */}
        <div className='text-center space-y-4'>
          <div className='flex items-center justify-center space-x-6 text-xs text-gray-500'>
            <div className='flex items-center space-x-1'>
              <span className='w-2 h-2 bg-green-500 rounded-full'></span>
              <span>Free Forever</span>
            </div>
            <div className='flex items-center space-x-1'>
              <span className='w-2 h-2 bg-blue-500 rounded-full'></span>
              <span>Instant Setup</span>
            </div>
            <div className='flex items-center space-x-1'>
              <span className='w-2 h-2 bg-purple-500 rounded-full'></span>
              <span>No Credit Card</span>
            </div>
          </div>
          <p className='text-xs text-gray-500 max-w-sm mx-auto'>
            By creating an account, you agree to our Terms of Service and
            Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
